import Vector from "victor";
import Rect from "../types/Rect";
import { ShapeDOMRepresentation } from "../types/ShapeDOMRepresentation";
import CanvasBasedDOMRepresentation from "@/wbcontroller/CanvasBasedDOMRepresentation";
import Camera from "../Camera";
import { Scope } from "@/wbcontroller/types/Scope";
import WhiteboardControllerInterface from "@/wbcontroller/types/WhiteboardControllerInterface";
import {
  AttributeKey,
  HasAspectRatio,
  PartialUnknownShape,
} from "@/wbcontroller/types/attributes";
import { SimpleShape2 } from "@/wbcontroller/standardShapes/StandardShapes";
import {
  getStandardShapeScreenToBoundedDelta,
  getStandardShapeBoundedToScreenDelta,
} from "@/wbcontroller/standardShapes/standardShapeConversions";
import { EditOperation } from "../operations/EditOperation";
import { getStandardPadding, needsZoomAdjustment, shapeScale2 } from "../util";
import { ShapeWithCanvasBasedDOMRepresentation2 } from "../types/Shape";

export abstract class DragHandle {
  el: HTMLDivElement;
  shapeDR: ShapeDOMRepresentation;
  original: PartialUnknownShape | null = null;

  constructor(
    shapeDR: ShapeDOMRepresentation,
    public shouldApplyConstraints = true
  ) {
    this.el = document.createElement("div");
    this.shapeDR = shapeDR;
    this.el.className = "SelectTool_DragHandle";
    this.el.dataset.isDragHandle = "true";
  }

  teardown() {
    this.el.parentElement?.removeChild(this.el);
  }

  getIsPointOnHandle(screenPoint: Vector) {
    const clientRect = Rect.fromBoundingClientRect(
      this.el.getBoundingClientRect()
    );
    return clientRect.contains(screenPoint);
  }

  abstract getLocalPoint(): Vector;
  abstract onPointerDown(
    screenPoint: Vector,
    wb: WhiteboardControllerInterface
  ): void;
  abstract onPointerDrag(
    screenPoint: Vector,
    wb: WhiteboardControllerInterface
  ): void;
  abstract onPointerUp(
    screenPoint: Vector,
    wb: WhiteboardControllerInterface
  ): void;

  constrain(point: Vector): Vector {
    return point;
  }

  applyTransform(camera: Camera) {
    const boundingRect = this.shapeDR.boundingRect;
    const selectionRect = this.shapeDR.selectionRect;
    const selectionRectOffset = boundingRect.min.subtract(selectionRect.min);

    // adjust scale if element is scalable
    const localPoint = this.getLocalPoint();
    if (needsZoomAdjustment(this.shapeDR.shape)) {
      const canvasBasedShape = this.shapeDR
        .shape as ShapeWithCanvasBasedDOMRepresentation2;
      const scale = new Vector(1, 1).divide(shapeScale2(canvasBasedShape));
      localPoint.multiply(scale);
    }
    const newPoint = camera.convertWorldToScreenScale(
      localPoint.add(selectionRectOffset)
    );
    const newLeft = `${newPoint.x - 7}px`;
    const newTop = `${newPoint.y - 7}px`;
    if (newLeft != this.el.style.left) this.el.style.left = newLeft;
    if (newTop != this.el.style.top) this.el.style.top = newTop;
  }
}

abstract class DragHandleSinglePoint extends DragHandle {
  abstract get attribute(): AttributeKey;
  private _blockedErasers = new Array<string>();

  get shape(): SimpleShape2 {
    return this.shapeDR.shape as unknown as SimpleShape2;
  }

  getLocalPoint(): Vector {
    const s = this.shapeDR.shape as unknown as Record<AttributeKey, unknown>;
    if (!s) return new Vector(0, 0);
    return Vector.fromArray((s[this.attribute] as [number, number]) || [0, 0]);
  }

  onPointerDown(screenPoint: Vector, wb: WhiteboardControllerInterface) {
    this.original = { ...this.shapeDR.shape };
    this._blockedErasers = this.shape.eraserBlocklist ?? [];
    const canvasDR = this.shapeDR as CanvasBasedDOMRepresentation;
    if (canvasDR) {
      for (const e of canvasDR.eraserRenderers) {
        this._blockedErasers.push(e.id);
      }
    }

    // Dedup values
    this._blockedErasers = [...new Set(this._blockedErasers)];

    // Before we do anything, we need to make the shape's canvas cover the whole
    // screen, otherwise the shape will be clipped to the original bounding
    // rect.
    const delta = getStandardShapeBoundedToScreenDelta(wb.camera, this.shape);
    // console.log(delta);
    wb.shapeStore.sendUpsertManyLive(this.shapeDR.id, {
      ...delta,
      [this.attribute]: [screenPoint.x, screenPoint.y],
      eraserBlocklist: this._blockedErasers,
    });
  }

  // eslint-disable-next-line
  onPointerDrag(screenPoint: Vector, wb: WhiteboardControllerInterface) {
    const constrainedPoint = this.shouldApplyConstraints
      ? this.constrain(screenPoint)
      : screenPoint;
    wb.shapeStore.sendUpsertSingleLive(this.shapeDR.id, {
      key: this.attribute,
      value: [constrainedPoint.x, constrainedPoint.y],
    });
  }

  // eslint-disable-next-line
  onPointerUp(screenPoint: Vector, wb: WhiteboardControllerInterface) {
    // Convert the shape back to having an efficient bounding
    const delta = getStandardShapeScreenToBoundedDelta(
      wb.camera,
      this.shape,
      getStandardPadding(this.shape)
    );
    // console.log(delta);

    wb.applyOperation(
      new EditOperation(
        this.shapeDR.id,
        this.shape.shapeType,
        this.original ?? this.shape,
        {
          ...delta,
          eraserBlocklist: this._blockedErasers,
        }
      )
    );
  }
}

export class DragHandlePointA extends DragHandleSinglePoint {
  get attribute() {
    return AttributeKey.PointA;
  }

  constrain(point: Vector): Vector {
    const a = point;
    const b = new Vector(...this.shape.pointB);
    const constrained1 = new Vector(Math.min(a.x, b.x), Math.min(a.y, b.y));
    const aspectRatio = (this.shape as HasAspectRatio).aspectRatio;
    if (!aspectRatio) return constrained1;
    const w = b.x - a.x;
    const h = b.y - a.y;
    const option1 = new Vector(a.x, b.y - w / aspectRatio);
    const option2 = new Vector(b.x - h * aspectRatio, a.y);
    // I thought the '<' should be a '>' but it was backwards so I changed it and moved on without thinking too hard
    if (option1.lengthSq() < option2.lengthSq()) {
      return option1;
    } else {
      return option2;
    }
  }
}

export class DragHandlePointB extends DragHandleSinglePoint {
  get attribute() {
    return AttributeKey.PointB;
  }

  constrain(point: Vector): Vector {
    const a = new Vector(...this.shape.pointA);
    const b = point;
    const constrained1 = new Vector(
      Math.max(point.x, this.shape.pointA[0]),
      Math.max(point.y, this.shape.pointA[1])
    );
    const aspectRatio = (this.shape as HasAspectRatio).aspectRatio;
    if (!aspectRatio) return constrained1;
    const w = b.x - a.x;
    const h = b.y - a.y;
    const option1 = new Vector(b.x, a.y + w / aspectRatio);
    const option2 = new Vector(a.x + h * aspectRatio, b.y);
    // console.log(option1, option2);
    if (option1.lengthSq() > option2.lengthSq()) {
      return option1;
    } else {
      return option2;
    }
  }
}

export class DragHandlePointAOrB extends DragHandleSinglePoint {
  polarity = false;
  constructor(
    shapeDR: ShapeDOMRepresentation,
    polarity: boolean,
    shouldApplyConstraints = true
  ) {
    super(shapeDR, shouldApplyConstraints);
    this.polarity = polarity;
  }

  // This variant always moves point A, and sets point B to whichever
  // the opposite corner is.
  get attribute() {
    return AttributeKey.PointA;
  }

  getLocalPoint(): Vector {
    if (this.polarity) {
      return new Vector(this.shape.pointA[0], this.shape.pointB[1]);
    } else {
      return new Vector(this.shape.pointB[0], this.shape.pointA[1]);
    }
  }

  onPointerDown(screenPoint: Vector, wb: WhiteboardControllerInterface) {
    super.onPointerDown(screenPoint, wb);

    if (this.polarity) {
      wb.shapeStore.sendUpsertMany(Scope.Local, this.shapeDR.id, {
        pointB: [this.shape.pointB[0], this.shape.pointA[1]],
      });
    } else {
      wb.shapeStore.sendUpsertMany(Scope.Local, this.shapeDR.id, {
        pointB: [this.shape.pointA[0], this.shape.pointB[1]],
      });
    }
  }
}
